import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, HostListener, inject, signal, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { BoltDirectivesModule } from '@nationwide-bolt/ng-directives';
import { OidcSecurityService } from 'angular-auth-oidc-client';

import { API_ENDPOINTS } from '@hsc/common/constants/endpoints';
import { AxiomQuoteLambdaResponse, AxiomSellerQuoteRequest } from '@hsc/common/schemas/axiom.schema';
import { JwtInfo } from '@hsc/common/schemas/eua.schema.js';
import { PcPolicyNumberLambdaResponse } from '@hsc/common/schemas/pc-management.schema';
import { PcSubmissionNumberLambdaResponse } from '@hsc/common/schemas/pc-submission.schema';
import { Card } from '@components/card';
import { ApigeeService } from '@services/apigee.service';
import { DeveloperToolsService } from '@services/developer-tools.service';
import { FullStoryService } from '@services/fullstory.service';
import { LaunchDarklyService } from '@services/launch-darkly.service';
import { SplunkLoggerService } from '@services/splunk-logger.service';
import { ApigeeError } from '@utilities/http-utilities';
import { environment } from '@environments/environment';

import { axiomQuoteSelectOptions, policyNumberSelectOptions, submissionNumberSelectOptions } from './select-options';

type DevToolsModalElement = HTMLElement & { openModal: () => void };

const jwtInfo: JwtInfo = {
  firstName: 'Hsc',
  iam_domain: 'partner-pnc-gsa',
  lastName: 'Agent',
  racfId: 'aha007',
  realm: 'distribution-partner',
  sub: 'aha007',
  userId: 'aha007',
};

@Component({
  selector: 'hsc-developer-tools',
  imports: [CommonModule, ReactiveFormsModule, Card, BoltDirectivesModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './developer-tools.html',
})
export class DeveloperTools {
  // Services
  private readonly apigeeService = inject(ApigeeService);
  private readonly developerToolsService = inject(DeveloperToolsService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly fullstoryService = inject(FullStoryService);
  private readonly launchDarklyService = inject(LaunchDarklyService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly splunkLoggerService = inject(SplunkLoggerService);

  // Service Properties
  readonly apigeeIsLoading = this.apigeeService.isLoading;
  readonly devToolsEmailOverride = this.developerToolsService.emailOverride;
  readonly flags = this.launchDarklyService.flags$;

  // Component Properties
  readonly apiEndpoints = API_ENDPOINTS;
  apiResponse = signal<null | string>(null);
  readonly axiomQuoteSelectOptions = axiomQuoteSelectOptions;
  readonly devToolsModal = viewChild.required<ElementRef<DevToolsModalElement>>('devToolsModal');
  readonly developerToolsForm = this.formBuilder.nonNullable.group({
    policyNumber: this.formBuilder.nonNullable.control(''),
    submissionNumber: this.formBuilder.nonNullable.control(''),
    axiomQuote: this.formBuilder.nonNullable.control(''),
    axiomEmailQuoteId: this.formBuilder.nonNullable.control(''),
    axiomEmailAddress: this.formBuilder.nonNullable.control(''),
  });
  readonly environment = environment;
  readonly policyNumberSelectOptions = policyNumberSelectOptions;
  readonly submissionNumberSelectOptions = submissionNumberSelectOptions;

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (
      event.altKey &&
      event.code === 'KeyD' &&
      this.launchDarklyService.getFlagValue('developer-tools-enabled', false)
    ) {
      this.devToolsModal()?.nativeElement.openModal();
    }
  }

  // Methods
  setApiResponse(value: unknown) {
    if (value instanceof ApigeeError) {
      const apigeeError = value.originalError?.error as { developerMessage?: unknown } | undefined;
      const developerMessage = apigeeError?.developerMessage;
      if (developerMessage) {
        this.apiResponse.set(
          typeof developerMessage === 'string' ? developerMessage : JSON.stringify(developerMessage),
        );
        return;
      }
    }
    const stringified = typeof value === 'string' ? value : JSON.stringify(value);
    this.apiResponse.set(stringified);
  }

  formatApiResponse(): string | null {
    const response = this.apiResponse();
    if (!response) return null;
    try {
      return JSON.stringify(JSON.parse(response), null, 2);
    } catch {
      return response;
    }
  }

  toggleEmailOverride() {
    this.devToolsEmailOverride.set(!this.devToolsEmailOverride());
  }

  handlePolicyNumber() {
    this.apigeeService
      .get<PcPolicyNumberLambdaResponse>(
        this.apiEndpoints.pcPolicyNumber.invoke(this.developerToolsForm.controls.policyNumber.value),
      )
      .subscribe({
        next: (response) => this.setApiResponse(response),
        error: (error: ApigeeError) => this.setApiResponse(error),
        complete: () => this.developerToolsForm.controls.policyNumber.reset(),
      });
  }

  handleSubmissionNumber() {
    this.apigeeService
      .get<PcSubmissionNumberLambdaResponse>(
        this.apiEndpoints.pcSubmissionNumber.invoke(this.developerToolsForm.controls.submissionNumber.value),
      )
      .subscribe({
        next: (response) => this.setApiResponse(response),
        error: (error: ApigeeError) => this.setApiResponse(error),
        complete: () => this.developerToolsForm.controls.submissionNumber.reset(),
      });
  }

  handleAxiomQuote() {
    const axiomQuoteRequest = JSON.parse(this.developerToolsForm.controls.axiomQuote.value) as AxiomSellerQuoteRequest;
    this.apigeeService
      .post<AxiomQuoteLambdaResponse>(this.apiEndpoints.axiomQuote.invoke(), axiomQuoteRequest)
      .subscribe({
        next: (response) => {
          // Set quoteId and email for subsequent email call
          this.developerToolsForm.controls.axiomEmailQuoteId.setValue(response.data.quoteId);
          this.developerToolsForm.controls.axiomEmailAddress.setValue(axiomQuoteRequest.customer.emailAddress);
          this.setApiResponse(response);
        },
        error: (error: ApigeeError) => this.setApiResponse(error),
        complete: () => this.developerToolsForm.controls.axiomQuote.reset(),
      });
  }

  handleAxiomEmail() {
    this.apigeeService
      .post(this.apiEndpoints.axiomEmail.invoke(this.developerToolsForm.controls.axiomEmailQuoteId.value), {
        email: this.developerToolsForm.controls.axiomEmailAddress.value,
      })
      .subscribe({
        next: (response) => this.setApiResponse(response),
        error: (error: ApigeeError) => this.setApiResponse(error),
        complete: () => {
          this.developerToolsForm.controls.axiomEmailQuoteId.reset();
          this.developerToolsForm.controls.axiomEmailAddress.reset();
        },
      });
  }

  handleNewRelicTesting(payload: { target: 'client' | 'serverless'; type: 'expected' | 'unhandled' }) {
    if (payload.target === 'client') {
      // "Client error expected" is treated as expected by filter-errors-for-newrelic.ts.
      // handle-error.ts uses that classification to decide whether to send the error to New Relic.
      throw new Error(`Client error ${payload.type}`);
    } else {
      // Serverless error tests are handled by serverless/main/lib/handlers/developer-tools.ts.
      // Payload value (expected | unhandled) selects which serverless error shape to simulate.
      this.apigeeService.post(this.apiEndpoints.developerTools.invoke(), { type: payload.type }).subscribe();
    }
  }

  handleLaunchDarklyTesting(method: 'identifyUser' | 'getFlagValue') {
    switch (method) {
      case 'identifyUser':
        this.launchDarklyService.identifyUser(jwtInfo);
        break;
      case 'getFlagValue':
        this.launchDarklyService.getFlagValue('developer-tools-enabled', false);
        break;
    }
  }

  handleFullstory(method: 'setUserVars' | 'getCurrentSessionUrl' | 'event') {
    switch (method) {
      case 'setUserVars': {
        this.fullstoryService.setUserVars(jwtInfo);
        this.splunkLoggerService.info('FullStory user vars set');
        break;
      }
      case 'getCurrentSessionUrl': {
        const url = this.fullstoryService.getCurrentSessionUrl();
        this.splunkLoggerService.info(`FullStory current session URL:`, { url });
        break;
      }
      case 'event': {
        this.fullstoryService.event('Test Event', { testProperty: 'testValue' });
        this.splunkLoggerService.info('FullStory event sent');
        break;
      }
    }
  }

  handleSplunkTesting(level: 'error' | 'warn' | 'info') {
    const message = 'Log something';
    const meta = { data: 'magic' };

    switch (level) {
      case 'error':
        this.splunkLoggerService.error(message, meta);
        break;
      case 'warn':
        this.splunkLoggerService.warn(message, meta);
        break;
      default:
        this.splunkLoggerService.info(message, meta);
        break;
    }
  }

  logout() {
    this.oidcSecurityService.logoffAndRevokeTokens().subscribe();
  }
}
